import { Users, CheckCircle, XCircle } from "lucide-react";
import React from "react";
import Modal from "./Modal";

const TeamMemberCard = ({ member, currentUserRole, currentUserId, onRemove, onMakeModerator = () => {}, onAccept = () => {}, onDecline = () => {} }) => {
    const [showDeclineModal, setShowDeclineModal] = React.useState(false);
    const isWithinLastThreeDays = (dateString) => {
        const date = new Date(dateString);
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        return date >= threeDaysAgo;
    };

    return (
        <div className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200
            ${member.pivot && !member.pivot.accepted ? 'border-l-4 border-yellow-400' : isWithinLastThreeDays(member.pivot.joined_at) ? 'border-l-4 border-blue-500' : ''}`}
        >
            <div className="p-3">
                <div className="rounded-t-lg -mx-3 -mt-3 px-3 py-2.5 mb-3 bg-gray-100 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <h3 className="font-medium m-0 p-0 text-sm text-gray-900 dark:text-white truncate">
                                {member.name}
                            </h3>
                            {isWithinLastThreeDays(member.pivot.joined_at) && (
                                <span className="px-1.5 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 rounded-full shrink-0">
                                    New
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 mb-4">
                    {member.moderator ? (
                        <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full">
                            Moderator
                        </span>
                    ) : member.pivot && !member.pivot.accepted ? (
                        <span className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 bg-yellow-200 dark:bg-yellow-900/40 text-yellow-900 dark:text-yellow-200 rounded-full font-semibold flex items-center gap-1 animate-blink">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 17a5 5 0 100-10 5 5 0 000 10z" /></svg>
                                Action Required
                            </span>
                        </span>
                    ) : (
                        (() => {
                            const role = member.pivot?.role;
                            if (role === 'owner') {
                                return (
                                    <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-full">
                                        Owner
                                    </span>
                                );
                            } else if (role === 'moderator') {
                                return (
                                    <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full">
                                        Moderator
                                    </span>
                                );
                            } else {
                                return (
                                    <span className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full">
                                        Team Member
                                    </span>
                                );
                            }
                        })()
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {(member.created_at || (member.pivot && member.pivot.joined_at)) ?
                            new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                .format(new Date(member.created_at || member.pivot?.joined_at))
                            : '—'}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                    {/* Challenges completed (left) */}
                    <div className="flex items-center gap-1" title="Challenges completed" aria-label="Challenges completed">
                        {/* Trophy icon */}
                        <svg className="w-4 h-4 text-yellow-500 dark:text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M8 21h8M12 17v4M17 5V3H7v2M17 5a5 5 0 1 1-10 0" />
                            <path d="M21 7c0 3.866-3.582 7-8 7s-8-3.134-8-7" />
                        </svg>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{member.team_challenges ?? 0}</span>
                    </div>
                    {/* Prompts created (right) */}
                    <div className="flex items-center gap-1" title="Prompts created" aria-label="Prompts created">
                        {/* Lightbulb icon */}
                        <svg className="w-4 h-4 text-sky-500 dark:text-sky-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-7 7c0 3.87 3.13 7 7 7s7-3.13 7-7a7 7 0 0 0-7-7z" />
                        </svg>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{member.team_prompts ?? 0}</span>
                    </div>
                </div>
            </div>
            <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 rounded-b-lg transition-colors duration-200">
                <div className="flex items-center justify-between gap-1">
                    {/* Left: Accept/Decline actions for requested members */}
                    {currentUserRole === 'moderator' || currentUserRole === 'owner' ? (
                        member.pivot && !member.pivot.accepted ? (
                            <div className="flex items-center">
                                <span className="text-xs mr-1 text-gray-500 dark:text-gray-400">Accept:</span>
                                <button
                                    onClick={() => onAccept(member.id)}
                                    title="Accept"
                                    className="p-1 hover:bg-green-100 dark:hover:bg-green-900 rounded-full transition-all duration-200"
                                >
                                    {/* Check icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-green-600 hover:text-green-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setShowDeclineModal(true)}
                                    title="Decline"
                                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded-full transition-all duration-200 ml-1"
                                >
                                    {/* X icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-red-600 hover:text-red-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                {showDeclineModal && (
                                    <Modal isOpen={showDeclineModal} onClose={() => setShowDeclineModal(false)}>
                                        <div className="text-center">
                                            <h2 className="text-lg font-semibold mb-4">Decline Team Member Request</h2>
                                            <p className="mb-6">Are you sure you want to decline this team member request?</p>
                                            <div className="flex justify-center gap-4">
                                                <button
                                                    className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                                                    onClick={() => setShowDeclineModal(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition"
                                                    onClick={() => { setShowDeclineModal(false); onDecline(member.id); }}
                                                >
                                                    Confirm
                                                </button>
                                            </div>
                                        </div>
                                    </Modal>
                                )}
                            </div>
                        ) : <div />
                    ) : null}
                    {/* Right: Mod/Remove actions */}
                    {(currentUserRole === 'moderator' || currentUserRole === 'owner') && (
                        <div className={`flex items-center gap-1
                            ${(member.requested === true || member.id === currentUserId || 
                              (currentUserRole === 'moderator' && member.pivot?.role === 'moderator' && member.id !== currentUserId))
                                ? 'opacity-50 grayscale pointer-events-none' : ''}
                        `}
                        aria-disabled={member.requested === true || member.id === currentUserId || (currentUserRole === 'moderator' && member.pivot?.role === 'moderator' && member.id !== currentUserId)}
                        title={currentUserRole === 'moderator' && member.pivot?.role === 'moderator' && member.id !== currentUserId ? 'Moderators cannot remove other moderators. Only owners can remove moderators.' : undefined}
                        >
                            {/* Moderator toggle button, disabled for owners */}
                            {member.pivot?.role === 'owner' ? (
                                <button
                                    title="Owners cannot be made or removed as moderators"
                                    className="p-1 rounded-full transition-all duration-200 opacity-60 cursor-not-allowed"
                                    disabled
                                    tabIndex={-1}
                                >
                                    <Users className="w-3.5 h-3.5 text-blue-300 dark:text-blue-700" />
                                </button>
                            ) : member.pivot?.role === 'moderator' ? (
                                <button
                                    onClick={() => onMakeModerator(member.id)}
                                    title={currentUserRole === 'moderator' ? 'Only owners can remove moderator status from a user.' : 'Remove Moderator'}
                                    className={`p-1 rounded-full transition-all duration-200 ${currentUserRole === 'moderator' ? 'opacity-50 grayscale pointer-events-none cursor-not-allowed' : 'hover:bg-white/80 dark:hover:bg-gray-700/80'}`}
                                    disabled={member.requested === true || member.id === currentUserId || currentUserRole === 'moderator'}
                                    tabIndex={member.requested === true || member.id === currentUserId || currentUserRole === 'moderator' ? -1 : 0}
                                >
                                    <Users className="w-3.5 h-3.5 text-blue-500 hover:text-blue-700" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => onMakeModerator(member.id)}
                                    title={currentUserRole === 'moderator' ? 'Only owners can make a user moderator.' : 'Make Moderator'}
                                    className={`p-1 rounded-full transition-all duration-200 ${currentUserRole === 'moderator' ? 'opacity-50 grayscale pointer-events-none cursor-not-allowed' : 'hover:bg-white/80 dark:hover:bg-gray-700/80'}`}
                                    disabled={member.requested === true || member.id === currentUserId || currentUserRole === 'moderator'}
                                    tabIndex={member.requested === true || member.id === currentUserId || currentUserRole === 'moderator' ? -1 : 0}
                                >
                                    {/* Shield icon (Lucide) */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-blue-500 hover:text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4l7 4v4c0 5.25-3.5 9.74-7 10-3.5-.26-7-4.75-7-10V8l7-4z" />
                                    </svg>
                                </button>
                            )}
                            {/* Remove button, disabled for owners and for current user */}
                            <button
                                onClick={() => onRemove(member.id)}
                                title={member.pivot?.role === 'owner' ? 'Owners cannot be removed from the team' : 'Remove from team'}
                                className={`p-1 rounded-full transition-all duration-200 ${member.pivot?.role === 'owner' || member.id === currentUserId ? 'opacity-60 cursor-not-allowed' : 'hover:bg-white/80 dark:hover:bg-gray-700/80'}`}
                                disabled={member.pivot?.role === 'owner' || (member.pivot && !member.pivot.accepted) || member.id === currentUserId}
                                tabIndex={member.pivot?.role === 'owner' || member.id === currentUserId ? -1 : (member.pivot && !member.pivot.accepted ? -1 : 0)}
                            >
                                <XCircle className={`w-3.5 h-3.5 ${member.pivot?.role === 'owner' ? 'text-red-300 dark:text-red-700' : 'text-red-500 hover:text-red-600'}`} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamMemberCard;
